import { minutesToMilliseconds, secondsToMilliseconds } from 'date-fns';
import { useAtomValue } from 'jotai';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useEndOfSessionToast } from '../../hooks/useEndOfSessionToast';
import { useTrackEvent } from '../../segment/segment';
import { EventTypeEnum } from '../../segment/types';
import { appointmentInfoAtom } from '../../state';
import { toast } from '../../toasts';
import { LeaveSessionNudgeToast } from './LeaveSessionNudgeToast';
import { usePatientSessionRecapAccess } from './usePatientSessionRecapAccess';

const NUDGE_PRIOR_END_TIME_MS = minutesToMilliseconds(2);
const TOAST_TIMEOUT = minutesToMilliseconds(1);
const PILOT_RESOLUTION_TIMEOUT_MS = secondsToMilliseconds(5);

export function useLeaveSessionNudgeToast() {
  const { providerShortId, patientShortId } = useParams();
  const { userViewed } = useTrackEvent();
  const appointmentInfo = useAtomValue(appointmentInfoAtom);
  const appointmentShortId = appointmentInfo?.shortId;
  const appointmentEndTime = appointmentInfo?.timeEnd;

  const { variant, isLoading } = usePatientSessionRecapAccess(patientShortId);

  // `useEndOfSessionToast` memoizes the `showToast` it fires on
  // `[appointmentEndTime, isInSession]` only, so the callback can capture a
  // stale `variant` if the pilot/preference queries resolve after the
  // appointment end time loads. Read the latest value from a ref instead.
  const variantRef = useRef(variant);
  variantRef.current = variant;

  // Same stale-closure hazard: `sessionInfoAtom` is populated by the session-info
  // query's `onCompleted`, which can land after the memoized callback is built.
  const appointmentShortIdRef = useRef(appointmentShortId);
  appointmentShortIdRef.current = appointmentShortId;

  const [hasWaitedForPilot, setHasWaitedForPilot] = useState(false);
  useEffect(function expirePilotResolutionWindow() {
    const timeoutId = setTimeout(() => setHasWaitedForPilot(true), PILOT_RESOLUTION_TIMEOUT_MS);
    return () => clearTimeout(timeoutId);
  }, []);

  const showToast = () => {
    const shownVariant = variantRef.current;

    userViewed(EventTypeEnum.SESSION_ENDING_SOON_BANNER_VIEWED, {
      appointmentShortId: appointmentShortIdRef.current ?? '',
      patientShortId: patientShortId ?? '',
      providerShortId: providerShortId ?? '',
      variant: shownVariant,
    });

    toast.enqueue(
      (ariaProps) => {
        return <LeaveSessionNudgeToast {...ariaProps} variant={shownVariant} />;
      },
      { timeout: TOAST_TIMEOUT, purpose: 'prompt' },
    );
  };

  // Defer firing until the pilot/preference queries resolve; otherwise the toast
  // (and Segment event) can fire once with the `legacy` variant, permanently
  // hiding the recap banner from pilot patients on slow networks. While this
  // gate is false, `useEndOfSessionToast` keeps polling without marking the
  // toast as shown.
  const isReadyToNudge = !isLoading || hasWaitedForPilot;

  useEndOfSessionToast(appointmentEndTime, showToast, NUDGE_PRIOR_END_TIME_MS, isReadyToNudge);
}
